import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Table from 'react-bootstrap/Table'
import TableError from 'shared/tables/table-error'
import TableLoading from 'shared/tables/table-loading'
import TableFooter from 'shared/tables/table-footer'
import MutedText from 'shared/forms/muted-text'
import { HighRiskPermissionBadge, SuperUserPermissionBadge } from 'shared/badges'
import { useEmployeePermissions } from 'api/hooks/use-employee-permissions'
import Category from 'duties/roles/category'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFolder } from '@fortawesome/free-solid-svg-icons'

function PermissionsTab({ employeeId, categories }) {
  const [searchParams] = useSearchParams()
  const category = searchParams.get('category')
  const highRisk = searchParams.get('is_high_risk')
  const superUser = searchParams.get('is_super_user')

  const [params, setParams] = useState({
    id: employeeId,
    is_high_risk: highRisk === 'true',
    is_super_user: superUser === 'true',
    is_expansive: true,
    category: category || '',
    page: 1,
    perPage: 25
  })

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      category: category || '',
      is_high_risk: highRisk === 'true',
      is_super_user: superUser === 'true'
    }))
  }, [category, highRisk, superUser])

  const { data, isSuccess, isLoading, isError } = useEmployeePermissions({
    params
  })

  return (
    <Card className="border">
      <Card.Header className="bg-white">
        <Row>
          <Col sm="auto">
            <div className="text-info">Permissions</div>
          </Col>
          <Col sm="auto">
            <Category
              params={params}
              setParams={setParams}
              categories={categories}
            />
          </Col>

          <Col sm="auto">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="show-expansive-values"
                onChange={(e) =>
                  setParams({ ...params, is_expansive: e.target.checked })
                }
                checked={params.is_expansive}
              />
              <label className="form-check-label" for="show-expansive-values">
                Expansive
              </label>
            </div>
          </Col>

          <Col sm="auto">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="show-high-risk-only"
                onChange={(e) =>
                  setParams({ ...params, is_high_risk: e.target.checked })
                }
                checked={params.is_high_risk}
              />
              <label className="form-check-label" for="show-high-risk-only">
                High Risk
              </label>
            </div>
          </Col>
          <Col sm="auto">
            <div className="form-check form-switch ms-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="show-superuser-only"
                onChange={(e) =>
                  setParams({ ...params, is_super_user: e.target.checked })
                }
                checked={params.is_super_user}
              />
              <label className="form-check-label" for="show-superuser-only">
                Super User
              </label>
            </div>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <Table responsive size="sm">
          <thead>
            <tr>
              <th>Category</th>
              <th>Permission</th>
              <th>Value</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <TableLoading columns={4} />}
            {isSuccess &&
              data.data.map((p) => (
                <tr key={`permissions-row-${p.employee_permission_id}`}>
                  <td>
                    <MutedText as="div">
                      <FontAwesomeIcon
                        icon={faFolder}
                        className="text-muted me-2"
                      />
                      {p.category_caption}
                    </MutedText>
                  </td>
                  <td>
                    {p.description}
                    {p.is_high_risk && (
                      <HighRiskPermissionBadge count={1} displayCount={false} />
                    )}
                    {p.name.includes('super') && (
                      <SuperUserPermissionBadge
                        count={1}
                        displayCount={false}
                      />
                    )}
                  </td>
                  <td>{p.value}</td>
                  <td>
                    <MutedText as="div">{p.role_name}</MutedText>
                  </td>
                </tr>
              ))}
            {isError && <TableError columns={3} />}
          </tbody>
        </Table>
        {isSuccess && (
          <TableFooter
            meta={data.meta}
            params={params}
            setParams={setParams}
            total={data.data.length}
          />
        )}
      </Card.Body>
    </Card>
  )
}

export default PermissionsTab
